import { ApolloCache, gql, useApolloClient } from "@apollo/client";
import { print } from "graphql";
import { useCallback, useRef, useState } from "react";

import { CourseQuery_instance_progress_lastView } from "../pages/Player/__generated__/CourseQuery";
import { env } from "../util/env";
import { Sans } from "../util/sdk";
import { getCurrentDateTimeWithoutMs, getTimestampFromSeconds } from "../util/time";
import { useAppState } from "./useAppState";
import { UPDATE_PROGRESS_QUERY, useCourseProgress } from "./useCourseProgress";
import { useTestMode } from "./useTestMode";

export const featureName = "progressView";
export const openActionType = "open";
export const noCourseError = "no course";

interface SlideProgress {
  slideId: string;
  resumeTime: string;
  dateTimeViewed: string;
}

const PLAYER_PROGRESS_KEY = "PLAYER_PROGRESS";

// Core function to update Apollo cache with slide progress data
function writeSlideCacheFragment(cache: ApolloCache<object>, slideView: CourseQuery_instance_progress_lastView): void {
  const cacheId = cache.identify({ __typename: "Slide", id: slideView.slideId });

  // First ensure the slide has proper structure by checking if it exists properly
  const currentSlideData = cache.readFragment({
    id: cacheId,
    fragment: gql`
      fragment FullSlide on Slide {
        id
        __typename
      }
    `,
  });

  // If the slide doesn't have proper structure, initialize it first
  if (
    !currentSlideData ||
    typeof currentSlideData !== "object" ||
    !("id" in currentSlideData) ||
    !("__typename" in currentSlideData)
  ) {
    cache.writeFragment({
      id: cacheId,
      fragment: gql`
        fragment SlideInit on Slide {
          id
          __typename
        }
      `,
      data: {
        id: slideView.slideId,
        __typename: "Slide",
      },
    });
  }

  // Now write the lastView data
  cache.writeFragment({
    id: cacheId,
    fragment: gql`
      fragment SlideLastView on Slide {
        lastView {
          slideId
          resumeTime
          dateTimeViewed
        }
      }
    `,
    data: {
      lastView: {
        slideId: slideView.slideId,
        resumeTime: slideView.resumeTime,
        dateTimeViewed: slideView.dateTimeViewed,
      },
    },
  });
}

// Blanket update Apollo cache with multiple slide progress data
function updateSlideCacheBulk(
  cache: ApolloCache<object>,
  slideViews: CourseQuery_instance_progress_lastView[] = [],
): void {
  slideViews.forEach(slideView => writeSlideCacheFragment(cache, slideView));
}

// Helper function to update Apollo cache with single slide progress data
function updateSlideCache(
  client: ReturnType<typeof useApolloClient>,
  slideView: CourseQuery_instance_progress_lastView,
): void {
  writeSlideCacheFragment(client.cache, slideView);
}

// Custom hook for localStorage-based slide progress
export function useSlideProgressStorage() {
  const [slideProgress, setSlideProgress] = useState<SlideProgress | undefined>(() => {
    try {
      const stored = localStorage.getItem(PLAYER_PROGRESS_KEY);
      return stored ? JSON.parse(stored) : undefined;
    } catch {
      return undefined;
    }
  });

  const updateSlideProgress = useCallback((progress: SlideProgress | undefined) => {
    try {
      if (progress) {
        localStorage.setItem(PLAYER_PROGRESS_KEY, JSON.stringify(progress));
      } else {
        localStorage.removeItem(PLAYER_PROGRESS_KEY);
      }
      setSlideProgress(progress);
    } catch (error) {
      console.warn("Failed to save slide progress to localStorage:", error);
      setSlideProgress(progress); // Still update state even if localStorage fails
    }
  }, []);

  return { slideProgress, updateSlideProgress };
}

export function useProgress() {
  const {
    appState: { courseId, slideId, instanceId },
    course,
  } = useAppState();
  const {
    progress,
    openCert,
    saveCompletion,
    updateProgress,
    expiration,
    completion,
    syncCourseSlideViews,
    buildCourseProgressPayload,
  } = useCourseProgress(courseId ?? undefined, course?.id, instanceId ?? undefined);
  const { testMode } = useTestMode();

  const client = useApolloClient();
  const { slideProgress, updateSlideProgress } = useSlideProgressStorage();

  const saveProgress = useCallback(
    async (moduleRefresh: boolean, seconds: number): Promise<void> => {
      if (!slideId || testMode || !courseId || !instanceId) return;

      const resumeTime = getTimestampFromSeconds(seconds);
      const dateTimeViewed = getCurrentDateTimeWithoutMs();

      const lastView = {
        slideId,
        resumeTime,
        dateTimeViewed,
      } as CourseQuery_instance_progress_lastView;

      const result = await updateProgress(moduleRefresh, lastView);

      // Blanket update Apollo cache with all slide data from response
      // Note: Type assertion needed until GraphQL types are regenerated with slides field
      if (
        result?.data?.updateProgress &&
        typeof result.data.updateProgress === "object" &&
        "slides" in result.data.updateProgress
      ) {
        const slides = (result.data.updateProgress as { slides: CourseQuery_instance_progress_lastView[] }).slides;
        updateSlideCacheBulk(client.cache, slides);
      }
      syncCourseSlideViews();
    },
    [slideId, courseId, instanceId, client, updateProgress, testMode, syncCourseSlideViews],
  );

  const postSaveProgressServiceWorkerMessage = useCallback(
    async (lastView: CourseQuery_instance_progress_lastView) => {
      const courseProgressPayload = buildCourseProgressPayload(lastView);

      if (!courseProgressPayload) {
        console.warn("Could not build course progress payload for service worker");
        return;
      }
      try {
        const tokens = await Sans.sdk.core.api.tokens.freshTokens();
        navigator.serviceWorker.controller.postMessage({
          action: "saveProgress",
          url: env.VITE_GRAPHQL_URI,
          token: tokens.id,
          query: print(UPDATE_PROGRESS_QUERY),
          variables: {
            courseProgress: courseProgressPayload,
            instanceId,
            courseId,
          },
        });
      } catch (error) {
        console.error("Failed to save progress via service worker:", error);
      }
    },
    [buildCourseProgressPayload, instanceId, courseId],
  );

  const saveProgressServiceWorker = useCallback(async (): Promise<void> => {
    if (
      !courseId ||
      !slideProgress ||
      !instanceId ||
      !("serviceWorker" in navigator) ||
      !navigator.serviceWorker.controller
    ) {
      return;
    }

    // Build the course progress payload with the current slide progress
    const lastView = {
      slideId: slideProgress.slideId,
      resumeTime: slideProgress.resumeTime,
      dateTimeViewed: slideProgress.dateTimeViewed,
    } as CourseQuery_instance_progress_lastView;

    postSaveProgressServiceWorkerMessage(lastView);
  }, [slideProgress, courseId, instanceId, postSaveProgressServiceWorkerMessage]);

  const lastSlideProgress = useRef<SlideProgress | undefined>(undefined);

  const syncLocalSlideProgress = useCallback(
    (slideId: string, seconds: number) => {
      const resumeTime = getTimestampFromSeconds(seconds);
      const dateTimeViewed = getCurrentDateTimeWithoutMs();

      const newProgress = { slideId, resumeTime, dateTimeViewed };
      const lastProgress = lastSlideProgress.current;

      // Only sync if data has meaningfully changed (slideId or resumeTime changed)
      if (
        !lastProgress ||
        lastProgress.slideId !== newProgress.slideId ||
        lastProgress.resumeTime !== newProgress.resumeTime // ||
        // Not updating if dateTimeViewed changed, because that has ms precision
        // lastProgress.dateTimeViewed !== newProgress.dateTimeViewed
      ) {
        updateSlideProgress(newProgress);
        lastSlideProgress.current = newProgress;

        // Update Apollo cache immediately for consistency
        updateSlideCache(client, {
          slideId,
          resumeTime,
          dateTimeViewed,
        } as CourseQuery_instance_progress_lastView);
      }
    },
    [client, updateSlideProgress],
  );

  const progressForModule = useCallback(
    (moduleId: string) => {
      return progress?.moduleProgress.find(module => module.id === moduleId);
    },
    [progress],
  );

  const progressForSection = useCallback(
    (sectionId: string) => {
      const sectionModules = course?.sections.find(section => section.id === sectionId)?.modules;
      const slides = sectionModules?.reduce(
        (acc, module) => {
          const progress = progressForModule(module.id);

          if (progress) {
            acc.viewedSlides += progress.viewedSlides;
            acc.totalSlides += progress.totalSlides;
          }
          return acc;
        },
        { viewedSlides: 0, totalSlides: 0 },
      );

      return {
        ...slides,
        percentSlides: slides?.totalSlides ? (slides.viewedSlides / slides.totalSlides) * 100 : 0,
      };
    },
    [progressForModule, course?.sections],
  );

  return {
    saveProgress,
    updateProgress,
    syncLocalSlideProgress,
    saveProgressServiceWorker,
    progress,
    openCert,
    saveCompletion,
    expiration,
    completion,
    progressForModule,
    progressForSection,
  };
}
